import { useState, useEffect } from "react";
import { API_URL } from "../../context/AuthContext";
import toSlug from "./toSlug";
import {
  Box,
  Typography,
  Alert,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import type { ProductData } from "./Dashboard";

interface ProductSectionProps {
  product: ProductData;
  token: string | null;
  onChange: () => void;
  onDelete: () => void;
  categories: Array<{ id: number; name: string }>;
}

export default function ProductSection({ product, token, onChange, onDelete, categories }: ProductSectionProps) {
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(product.slug);
  const [title, setTitle] = useState(product.title);
  const [subtitle, setSubtitle] = useState(product.subtitle || "");
  const [description, setDescription] = useState(product.description || "");
  const [titleEn, setTitleEn] = useState(product.title_en || "");
  const [subtitleEn, setSubtitleEn] = useState(product.subtitle_en || "");
  const [categoryId, setCategoryId] = useState(product.category_id || "");
  const [translating, setTranslating] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const handleTitleChange = (val: string) => {
    setTitle(val);
    setSlug(toSlug(val));
  };

  useEffect(() => {
    setSlug(product.slug);
    setTitle(product.title);
    setSubtitle(product.subtitle || "");
    setDescription(product.description || "");
    setTitleEn(product.title_en || "");
    setSubtitleEn(product.subtitle_en || "");
    setCategoryId(product.category_id || "");
    setEditing(false);
    setMsg("");
    setError("");
  }, [product]);

  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const handleSave = async () => {
    setError("");
    if (!slug || !title) {
      setError("Slug i title są wymagane");
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/products/${product.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ slug, title, subtitle, description, title_en: titleEn, subtitle_en: subtitleEn, category_id: categoryId || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setEditing(false);
      setMsg("Zapisano");
      onChange();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleTranslate = async () => {
    setTranslating(true);
    setError("");
    try {
      const texts = [title, subtitle].filter(Boolean);
      const results = await Promise.all(
        texts.map((t) =>
          fetch(`${API_URL}/api/translate`, {
            method: "POST",
            headers,
            body: JSON.stringify({ text: t }),
          }).then((r) => r.json())
        )
      );
      if (results[0].text) setTitleEn(results[0].text);
      if (results[1] && results[1].text) setSubtitleEn(results[1].text);
      setMsg("Przetłumaczono — kliknij Zapisz aby zapisać");
    } catch {
      setError("Błąd tłumaczenia");
    } finally {
      setTranslating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Usunąć produkt "${product.title}"? Tej operacji nie można cofnąć.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/products/${product.id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Błąd usuwania");
      onDelete();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg("")}>{msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      {editing ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: "#aaa" }}>Kategoria *</InputLabel>
            <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} label="Kategoria *" sx={{ color: "#f0f0f0" }}>
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" sx={{ color: "#1976d2", fontWeight: 700 }}>PL</Typography>
          <TextField size="small" label="Tytuł PL" value={title} onChange={(e) => handleTitleChange(e.target.value)} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} />
          {slug && <Typography variant="caption" sx={{ color: "#888", mt: -1 }}>Adres: /oferta/{slug}</Typography>}
          <TextField size="small" label="Podtytuł PL" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} multiline rows={2} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} />
          <TextField size="small" label="Opis" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={4} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
            <Typography variant="caption" sx={{ color: "#4caf50", fontWeight: 700 }}>EN</Typography>
            <Button size="small" onClick={handleTranslate} disabled={translating} sx={{ color: "#ff9800" }}>
              {translating ? "Tłumaczenie..." : "Tłumacz na angielski"}
            </Button>
          </Box>
          <TextField size="small" label="Tytuł EN" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} />
          <TextField size="small" label="Podtytuł EN" value={subtitleEn} onChange={(e) => setSubtitleEn(e.target.value)} multiline rows={2} InputProps={{ sx: { color: "#f0f0f0" } }} InputLabelProps={{ sx: { color: "#aaa" } }} />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" onClick={handleSave} sx={{ color: "#4caf50" }}>Zapisz</Button>
            <Button size="small" onClick={() => { setEditing(false); setSlug(product.slug); setTitle(product.title); setSubtitle(product.subtitle || ""); setDescription(product.description || ""); setTitleEn(product.title_en || ""); setSubtitleEn(product.subtitle_en || ""); setCategoryId(product.category_id || ""); setError(""); }} sx={{ color: "#aaa" }}>Anuluj</Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 120 }}>Kategoria:</Typography>
            <Typography sx={{ color: "#f0f0f0" }}>{categories.find((c) => c.id === product.category_id)?.name || "—"}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 120 }}>Adres URL:</Typography>
            <Typography sx={{ color: "#f0f0f0" }}>{product.slug}</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "#1976d2", fontWeight: 700, mt: 1 }}>PL</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 80 }}>Tytuł:</Typography>
            <Typography sx={{ color: "#f0f0f0" }}>{product.title}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 80 }}>Podtytuł:</Typography>
            <Typography sx={{ color: "#f0f0f0" }}>{product.subtitle || "—"}</Typography>
          </Box>
          <Typography variant="caption" sx={{ color: "#4caf50", fontWeight: 700, mt: 1 }}>EN</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 80 }}>Tytuł:</Typography>
            <Typography sx={{ color: "#f0f0f0" }}>{product.title_en || "—"}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 80 }}>Podtytuł:</Typography>
            <Typography sx={{ color: "#f0f0f0" }}>{product.subtitle_en || "—"}</Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <Typography sx={{ color: "#aaa", minWidth: 80 }}>Opis:</Typography>
            <Typography sx={{ color: "#f0f0f0", whiteSpace: "pre-wrap" }}>{product.description || "—"}</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ color: "#1976d2" }}>Edytuj</Button>
            <Button size="small" startIcon={<DeleteIcon />} onClick={handleDelete} sx={{ color: "#f44336" }}>Usuń produkt</Button>
          </Box>
        </Box>
      )}
    </Box>
  );
}
